/**
 * The box model: what the form can describe, what it means in millimetres,
 * and the solid it becomes. Pure functions only — the page, the preview and
 * the 3MF writer all read from here, so they cannot disagree about a box.
 */

/** Each type is its own page; the route, not the form, says which. */
export type BoxType = 'modular' | 'custom';

/** Walls and floor share one thickness, in mm. */
export const WALLS = [0.8, 1.2] as const;
export type Wall = (typeof WALLS)[number];

export const DIMS = ['length', 'width', 'height'] as const;
type Dim = (typeof DIMS)[number];

/** Modular boxes tile on this grid, in mm. */
const GRID = 25;
const MODULAR_MIN = 25;
export const CUSTOM_MIN = 10;
export const MAX = 250;
/** Taken off a modular box's length and width so neighbours don't bind; none yet. */
const CLEARANCE = 0;

/** Every size a modular dimension can take: 25, 50, … 250. */
export const GRID_SIZES = Array.from(
  { length: (MAX - MODULAR_MIN) / GRID + 1 },
  (_, i) => MODULAR_MIN + i * GRID,
);

export interface Box {
  type: BoxType;
  length: number;
  width: number;
  height: number;
  wall: Wall;
}

/**
 * What the form holds. Dimensions stay the raw input strings, so a value the
 * user is halfway through typing is never rewritten under them.
 */
export interface BoxForm {
  length: string;
  width: string;
  height: string;
  wall: Wall;
}

export const DEFAULT_FORM: BoxForm = {
  length: '50',
  width: '50',
  height: '25',
  wall: 1.2,
};

/** Rounds to 0.001 mm, so 50 − 2 × 1.2 reads 47.6 and not 47.599999… */
const mm = (value: number) => Math.round(value * 1000) / 1000;

function parseDim(type: BoxType, raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw);
  if (type === 'modular') return GRID_SIZES.includes(value) ? value : null;
  return value >= CUSTOM_MIN && value <= MAX ? value : null;
}

export type BoxParse =
  { box: Box; invalid: null } | { box: null; invalid: Set<Dim> };

/** A valid box, or the dimensions that keep the form from being one. */
export function parseBox(type: BoxType, form: BoxForm): BoxParse {
  const invalid = new Set<Dim>();
  const values = {} as Record<Dim, number>;
  for (const dim of DIMS) {
    const value = parseDim(type, form[dim]);
    if (value === null) invalid.add(dim);
    else values[dim] = value;
  }
  if (invalid.size > 0) return { box: null, invalid };
  return {
    box: { type, wall: form.wall, ...values },
    invalid: null,
  };
}

/** The box's outside in mm, after any clearance. */
export function outerSize(box: Box): { x: number; y: number; z: number } {
  const clearance = box.type === 'modular' ? CLEARANCE : 0;
  return {
    x: mm(box.length - clearance),
    y: mm(box.width - clearance),
    z: box.height,
  };
}

/** The cavity: two walls off length and width, the floor off height. */
export function innerSize(box: Box): { x: number; y: number; z: number } {
  const outer = outerSize(box);
  return {
    x: mm(outer.x - 2 * box.wall),
    y: mm(outer.y - 2 * box.wall),
    z: mm(outer.z - box.wall),
  };
}

/** `boxer-50x50x50-1.2mm` — the file and the object in it, in every language. */
export function boxName(box: Box): string {
  return `boxer-${box.length}x${box.width}x${box.height}-${box.wall.toFixed(1)}mm`;
}

export type Vec3 = [number, number, number];

export interface Mesh {
  vertices: Vec3[];
  triangles: Vec3[];
}

/**
 * The open box as one closed solid: 16 vertices, 28 triangles, no CSG.
 * Corners 0–3 are the outer floor, 4–7 the outer rim, 8–11 the inner rim and
 * 12–15 the inner floor, each counter-clockwise from above starting at the
 * origin. Every quad is listed counter-clockwise as seen from outside the
 * solid, which is how 3MF reads the outward normal.
 */
export function boxMesh(box: Box): Mesh {
  const { x, y, z } = outerSize(box);
  const t = box.wall;
  const ring = (x0: number, y0: number, x1: number, y1: number, h: number) =>
    [
      [x0, y0, h],
      [x1, y0, h],
      [x1, y1, h],
      [x0, y1, h],
    ] as Vec3[];
  const vertices = [
    ...ring(0, 0, x, y, 0),
    ...ring(0, 0, x, y, z),
    ...ring(t, t, x - t, y - t, z),
    ...ring(t, t, x - t, y - t, t),
  ].map((v) => v.map(mm) as Vec3);

  const quads: [number, number, number, number][] = [
    [0, 3, 2, 1], // outer floor, facing down
    [12, 13, 14, 15], // inner floor, facing up into the box
  ];
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    quads.push(
      [i, j, 4 + j, 4 + i], // outer wall
      [4 + i, 4 + j, 8 + j, 8 + i], // rim
      [12 + i, 8 + i, 8 + j, 12 + j], // inner wall, facing the cavity
    );
  }
  const triangles = quads.flatMap(
    ([a, b, c, d]) =>
      [
        [a, b, c],
        [a, c, d],
      ] as Vec3[],
  );
  return { vertices, triangles };
}
